// Unsaved planning work, kept in the browser (IndexedDB) so a crash, a reload or a closed tab doesn't lose it
// (spec §7.4). Each browser tab keeps one record: what's open on the Plan map that isn't saved, the drafts' JSON
// and the route being edited. It is written half a second after each change, and straight away when the tab is
// hidden or closed. On the next start, records from tabs that are no longer running - each running tab holds a
// Web Lock named after it - are put back, then removed. Records belong to the signed-in user: only theirs are
// recovered, and signing out clears them. Without IndexedDB (some private windows) everything works as before,
// just without this.
import { createStore, del, entries, set as put } from 'idb-keyval';
import type { UseStore } from 'idb-keyval';
import { useSyncExternalStore } from 'react';
import type { Draft } from './drafts';
import { allDrafts, getDraft, hasUnsavedChanges, setDraft, subscribeDrafts } from './drafts';
import type { RouteEdit } from './editing';
import { getEditSession, resumeEditing, subscribeEditSession } from './editSession';
import type { OpenTrailsState, TrailRef } from './openTrails';
import { getOpenTrails, restoreTrails, subscribeOpenTrails } from './openTrails';
import type { ApiTrail } from './trail';

export interface StoredTab {
  version: 1;
  user: string;
  tab: string;
  savedAt: number;
  /** Open trails that have drafts, with their colours. */
  open: OpenTrailsState['open'];
  drafts: [string, Draft][];
  editing?: { guid: string; base: ApiTrail; present: RouteEdit };
}

/** What was put back, for the Plan page's "Restored … from your last session". */
export interface Recovered {
  refs: TrailRef[];
}

const TAB_KEY = 'xped.plan.tab';
const LOCK_PREFIX = 'xped-plan-tab:';
const WRITE_DELAY_MS = 500;
const MAX_AGE_MS = 30 * 24 * 3600_000;
/** Without Web Locks, another tab's record counts as left behind once it hasn't changed for this long. */
const UNLOCKED_STALE_MS = 10 * 60_000;

let db: UseStore | null = null;
let user: string | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;
let stop: (() => void) | undefined;
let recovered: Recovered | null = null;
const recoveredListeners = new Set<() => void>();

/** This tab's id: kept for the tab (sessionStorage), so a reload finds its own record again. */
function tabId(): string {
  try {
    const existing = sessionStorage.getItem(TAB_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(TAB_KEY, id);
    return id;
  } catch {
    return (tabIdFallback ??= crypto.randomUUID());
  }
}
let tabIdFallback: string | undefined;

function openDb(): UseStore | null {
  try {
    return (db ??= createStore('xped-plan', 'drafts'));
  } catch {
    return null;
  }
}

/** This tab's record, or null when there's nothing unsaved. */
export function snapshot(): Omit<StoredTab, 'user' | 'savedAt'> | null {
  const drafts = allDrafts();
  const session = getEditSession();
  if (drafts.length === 0) return null;
  const withDrafts = new Set(drafts.map(([guid]) => guid));
  return {
    version: 1,
    tab: tabId(),
    open: getOpenTrails().open.filter((o) => withDrafts.has(o.guid)),
    drafts,
    editing:
      session && withDrafts.has(session.guid)
        ? { guid: session.guid, base: session.base, present: session.history.present }
        : undefined,
  };
}

/** Writes this tab's record now (or removes it when nothing is unsaved). */
export async function flushDraftStore(): Promise<void> {
  clearTimeout(timer);
  timer = undefined;
  const store = openDb();
  if (!store || !user) return;
  const current = snapshot();
  try {
    if (current) await put(tabId(), { ...current, user, savedAt: Date.now() } satisfies StoredTab, store);
    else await del(tabId(), store);
  } catch {
    // Storage full or refused: carry on without the copy.
  }
}

const schedule = () => {
  clearTimeout(timer);
  timer = setTimeout(() => void flushDraftStore(), WRITE_DELAY_MS);
};

/** Holds this tab's lock for as long as the tab runs, so other tabs know its record is in use. */
function holdLock() {
  void navigator.locks
    ?.request(LOCK_PREFIX + tabId(), () => new Promise<void>(() => undefined))
    .catch(() => undefined);
}

async function runningTabs(): Promise<Set<string> | null> {
  if (!navigator.locks?.query) return null;
  try {
    const { held = [] } = await navigator.locks.query();
    return new Set(
      held
        .map((l) => l.name ?? '')
        .filter((n) => n.startsWith(LOCK_PREFIX))
        .map((n) => n.slice(LOCK_PREFIX.length)),
    );
  } catch {
    return null;
  }
}

/** Puts back the user's work from tabs that are no longer running (this tab's own, after a reload, included). */
async function recover(store: UseStore, me: string): Promise<Recovered | null> {
  let records: [IDBValidKey, StoredTab][];
  try {
    records = await entries<IDBValidKey, StoredTab>(store);
  } catch {
    return null;
  }
  const now = Date.now();
  const running = await runningTabs();
  const ownTab = tabId();
  const refs: TrailRef[] = [];
  for (const [key, record] of records) {
    if (record?.version !== 1 || now - record.savedAt > MAX_AGE_MS) {
      await del(key, store).catch(() => undefined);
      continue;
    }
    const leftBehind =
      record.tab === ownTab ||
      (running ? !running.has(record.tab) : now - record.savedAt > UNLOCKED_STALE_MS);
    if (record.user !== me || !leftBehind) continue;

    for (const [guid, draft] of record.drafts) {
      if (!getDraft(guid)) setDraft(guid, draft.kind, draft.json);
    }
    restoreTrails(record.open);
    if (record.editing && !getEditSession() && record.open.some((o) => o.guid === record.editing!.guid)) {
      resumeEditing(record.editing.guid, record.editing.base, record.editing.present);
    }
    refs.push(
      ...record.open.filter((o) => hasUnsavedChanges(o.guid)).map(({ kind, guid }) => ({ kind, guid })),
    );
    // Now this tab's: its own record is rewritten below; another tab's goes.
    if (record.tab !== ownTab) await del(key, store).catch(() => undefined);
  }
  return refs.length ? { refs } : null;
}

/**
 * Starts keeping this tab's unsaved work for `userKey`, after putting back anything left from a crashed or
 * closed tab. Resolves with what was put back (null if nothing). Calling it again does nothing.
 */
export async function startDraftStore(userKey: string): Promise<Recovered | null> {
  if (stop) return null;
  const store = openDb();
  user = userKey;
  const unsubscribe = [
    subscribeDrafts(schedule),
    subscribeOpenTrails(schedule),
    subscribeEditSession(schedule),
  ];
  const onHide = () => {
    if (document.visibilityState === 'hidden') void flushDraftStore();
  };
  const onPageHide = () => void flushDraftStore();
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', onPageHide);
  stop = () => {
    unsubscribe.forEach((u) => u());
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('pagehide', onPageHide);
  };
  if (!store) return null;
  holdLock();
  const found = await recover(store, userKey);
  await flushDraftStore();
  if (found) {
    recovered = found;
    for (const listener of recoveredListeners) listener();
  }
  return found;
}

/** Removes every record of this user (signing out). */
export async function clearDraftStore(userKey: string): Promise<void> {
  const store = openDb();
  if (!store) return;
  try {
    for (const [key, record] of await entries<IDBValidKey, StoredTab>(store)) {
      if (record?.user === userKey) await del(key, store);
    }
  } catch {
    // Nothing more can be done.
  }
}

/** What the last start put back, until the Plan page's notice is dismissed. */
export function useRecovered(): Recovered | null {
  return useSyncExternalStore(
    (listener) => {
      recoveredListeners.add(listener);
      return () => recoveredListeners.delete(listener);
    },
    () => recovered,
  );
}

export function dismissRecovered() {
  recovered = null;
  for (const listener of recoveredListeners) listener();
}

/** For tests: forget everything, as a fresh page would. */
export function resetDraftStoreForTests() {
  stop?.();
  stop = undefined;
  clearTimeout(timer);
  user = null;
  recovered = null;
  db = null;
}
